import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { PROVIDER_REASONING_LEVELS, reasoningLabel } from "../../lib/reasoning";
import { SavedNote } from "../shared/Icons";
import { toastError } from "../shared/toast";

interface ReportsConfig {
  prompt: string;
  provider: string;
  model: string;
  reasoning: string;
  message_cap: number;
  lead_cap: number;
  max_tokens: number;
}

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
  thinking_levels: string[];
}

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta"];
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google", grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta",
};

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 };

export function ReportsConfigTab() {
  const qc = useQueryClient();

  const { data: config, isLoading } = useQuery<ReportsConfig>({
    queryKey: ["dev-reports-config"],
    queryFn: () => api.get("/dev/reports-config"),
  });

  const { data: models = [] } = useQuery<AvailableModel[]>({
    queryKey: ["dev-available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });

  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("deepseek");
  const [model, setModel] = useState("");
  const [reasoning, setReasoning] = useState("off");
  const [messageCap, setMessageCap] = useState("100");
  const [leadCap, setLeadCap] = useState("200");
  const [maxTokens, setMaxTokens] = useState("65536");
  const [loaded, setLoaded] = useState(false);

  // Hidrata o form quando o GET retorna (uma vez).
  useEffect(() => {
    if (!config || loaded) return;
    setPrompt(config.prompt ?? "");
    setProvider(config.provider || "deepseek");
    setModel(config.model ?? "");
    setReasoning(config.reasoning ?? "off");
    setMessageCap(String(config.message_cap ?? 100));
    setLeadCap(String(config.lead_cap ?? 200));
    setMaxTokens(String(config.max_tokens ?? 65536));
    setLoaded(true);
  }, [config, loaded]);

  const providerModels = models.filter((m) => m.provider === provider);
  const levels = PROVIDER_REASONING_LEVELS[provider] ?? [];
  const reasoningOptions = levels.includes("off") ? levels : ["off", ...levels];

  const mut = useMutation({
    mutationFn: () => {
      // PATCH só com os campos alterados em relação ao GET.
      const body: Partial<ReportsConfig> = {};
      if (config) {
        if (prompt !== config.prompt) body.prompt = prompt;
        if (provider !== config.provider) body.provider = provider;
        if (model !== config.model) body.model = model;
        if (reasoning !== config.reasoning) body.reasoning = reasoning;
        const mc = parseInt(messageCap, 10);
        if (Number.isFinite(mc) && mc !== config.message_cap) body.message_cap = mc;
        const lc = parseInt(leadCap, 10);
        if (Number.isFinite(lc) && lc !== config.lead_cap) body.lead_cap = lc;
        const mt = parseInt(maxTokens, 10);
        if (Number.isFinite(mt) && mt !== config.max_tokens) body.max_tokens = mt;
      }
      return api.patch("/dev/reports-config", body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dev-reports-config"] }),
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao salvar a configuração dos relatórios."),
  });

  function changeProvider(p: string) {
    setProvider(p);
    const first = models.find((m) => m.provider === p);
    setModel(first?.model_name ?? "");
    const lv = PROVIDER_REASONING_LEVELS[p] ?? [];
    if (!lv.includes(reasoning) && reasoning !== "off") setReasoning("off");
  }

  if (isLoading) {
    return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</div>;
  }

  return (
    <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Relatórios de análise de conversas</span>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>
          Config global dos relatórios da aba Relatórios → Análises de conversas: prompt padrão, modelo e limites de volume.
        </div>
      </div>

      <div>
        <label style={lbl}>Prompt padrão de análise (pré-preenchido no form; a gestora pode ajustar por execução)</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          spellCheck={false}
          style={{ ...inp, height: 320, resize: "vertical", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}
        />
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Provedor</label>
          <select style={inp} value={provider} onChange={(e) => changeProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
          </select>
        </div>
        <div style={{ flex: 2 }}>
          <label style={lbl}>Modelo</label>
          {providerModels.length > 0 ? (
            <select style={inp} value={model} onChange={(e) => setModel(e.target.value)}>
              {/* Placeholder: sem ele, select controlado com value="" MOSTRA o 1º
                  modelo sem estar selecionado de fato — botão Salvar fica travado. */}
              {!model && <option value="">Selecione…</option>}
              {!providerModels.some((m) => m.model_name === model) && model && (
                <option value={model}>{model}</option>
              )}
              {providerModels.map((m) => (
                <option key={m.id} value={m.model_name}>{m.label}</option>
              ))}
            </select>
          ) : (
            <input
              style={inp}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="ex: deepseek-chat"
              spellCheck={false}
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Raciocínio</label>
          <select style={inp} value={reasoning} onChange={(e) => setReasoning(e.target.value)}>
            {reasoningOptions.map((t) => (
              <option key={t} value={t}>{reasoningLabel(t)}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Mensagens por conversa (10–500)</label>
          <input
            style={inp}
            type="number"
            min={10}
            max={500}
            value={messageCap}
            onChange={(e) => setMessageCap(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Leads por relatório (1–1000)</label>
          <input
            style={inp}
            type="number"
            min={1}
            max={1000}
            value={leadCap}
            onChange={(e) => setLeadCap(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Tokens de saída (1024–384000)</label>
          <input
            style={inp}
            type="number"
            min={1024}
            max={384000}
            value={maxTokens}
            onChange={(e) => setMaxTokens(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !model.trim()}
          style={{ padding: "8px 20px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer", alignSelf: "flex-start" }}
        >
          {mut.isPending ? "Salvando..." : "Salvar"}
        </button>
        <SavedNote show={mut.isSuccess} />
      </div>
    </div>
  );
}
